<div class="shell-container" [class.sidebar-collapsed]="sidebarCollapsed()">
  <!-- Sticky top header -->
  <header class="top-header">
    <div class="header-left">
      <button mat-icon-button class="hamburger-btn" (click)="toggleMobileNav()" aria-label="Toggle navigation">
        <mat-icon>{{ mobileNavOpen() ? 'close' : 'menu' }}</mat-icon>
      </button>
      <a class="sitename" [routerLink]="wellsActive() ? null : '/home'">
        <img alt="Chevron logo" class="cvx-img" src="/assets/images/CvxLogo.svg" />
        <span class="brand-text">
          <span class="app-name">QMS</span>
          <span class="app-subtitle">Quality Management System</span>
        </span>
      </a>
    </div>

    @if (!wellsActive()) {
    <div class="header-search">
      <mat-icon class="search-icon">search</mat-icon>
      <input
        type="search"
        class="search-input"
        [value]="searchQuery()"
        (input)="searchQuery.set($any($event.target).value)"
        (keydown.enter)="onSearchSubmit()"
        placeholder="Search projects, documents, NCRs, inspections..."
        aria-label="Global search" />
    </div>
    }

    <div class="header-right">
      @if (!wellsActive()) {
      <a class="project-badge" [class.all-projects]="!hasProjectSelected()" routerLink="/projects"
         [matTooltip]="hasProjectSelected() ? 'Current project — click to change' : 'Click to choose a project'">
        <mat-icon class="project-badge-icon">{{ hasProjectSelected() ? 'folder_open' : 'folder_special' }}</mat-icon>
        <span class="project-badge-label">{{ projectBadgeLabel() }}</span>
      </a>

      <button mat-icon-button matTooltip="Notifications" (click)="toggleNotifications()" class="notif-btn"
              [matBadge]="notifCount()" [matBadgeHidden]="notifCount() === 0" matBadgeColor="warn" matBadgeSize="small">
        <mat-icon>notifications</mat-icon>
      </button>

      <button mat-icon-button matTooltip="Glossary, Abbreviations &amp; Help" (click)="openGlossary()" aria-label="Help and glossary">
        <mat-icon>help_outline</mat-icon>
      </button>
      }

      @if (currentUser(); as user) {
        <button class="user-chip" [matMenuTriggerFor]="userMenu" aria-label="Account menu">
          <span class="avatar">{{ userInitials() }}</span>
          <span class="user-meta">
            <span class="user-name">{{ user.displayName }}</span>
            <span class="user-role">{{ roleLabel() }}</span>
          </span>
          <mat-icon class="chevron">arrow_drop_down</mat-icon>
        </button>
        <mat-menu #userMenu>
          @if ((!wellsActive() && (isAdmin() || hasMultipleRoles())) ||
            (!supplierActive() && canSwitchWellsContext() && wellsContext?.allowed() && wellsContext?.canUseLegacyContext())) {
            <a mat-menu-item routerLink="/switch-role">
              <mat-icon>switch_account</mat-icon>
              <span>Switch Role View</span>
            </a>
          }
          @if (!wellsActive()) {
          <a mat-menu-item routerLink="/clear">
            <mat-icon>cached</mat-icon>
            <span>Clear Cache</span>
          </a>
          }
          <button mat-menu-item (click)="logout()">
            <mat-icon>logout</mat-icon>
            <span>Sign Out</span>
          </button>
        </mat-menu>
      }
    </div>
  </header>

  <!-- Notifications panel -->
  @if (!wellsActive() && notifOpen()) {
    <div class="notif-overlay" (click)="closeNotifications()"></div>
    <div class="notif-panel">
      <div class="notif-header">
        <span>Action Items ({{ notifCount() }})</span>
        <a mat-button routerLink="/my-assignments" (click)="closeNotifications()" class="notif-view-all">View all</a>
        <button mat-icon-button (click)="closeNotifications()"><mat-icon>close</mat-icon></button>
      </div>
      @if (notifLoading()) {
        <div class="notif-empty">
          <mat-icon>sync</mat-icon>
          <span>Loading notifications...</span>
        </div>
      } @else if (notifications().length === 0) {
        <div class="notif-empty">
          <mat-icon>check_circle</mat-icon>
          <span>No items requiring your attention — all clear!</span>
        </div>
      } @else {
        <div class="notif-list">
          @for (item of notifications(); track item.id) {
            <a class="notif-item" [class]="item.severity" [routerLink]="item.route" (click)="closeNotifications()">
              <mat-icon class="notif-icon">{{ item.severity === 'critical' ? 'error' : 'warning' }}</mat-icon>
              <div class="notif-body">
                <span class="notif-module">{{ item.module }} · {{ item.code }}</span>
                <span class="notif-title">{{ item.message }}</span>
                @if (item.dueDate) {
                  <span class="notif-due">Due: {{ item.dueDate }}</span>
                }
              </div>
            </a>
          }
        </div>
      }
    </div>
  }

  <div class="shell-body">
    <!-- Left sidebar -->
    <aside class="sidebar" [class.mobile-open]="mobileNavOpen()">
      <nav class="sidebar-nav" aria-label="Primary">
        <ul class="nav-list">
          @for (item of primaryNav(); track item.label) {
            @if (!item.adminOnly || isAdmin()) {
              <li>
                <a class="nav-link"
                   [class.nav-link--unavailable]="!item.route"
                   [attr.aria-disabled]="!item.route ? 'true' : null"
                   [attr.aria-label]="!item.route ? item.label + ' - Coming soon' : null"
                   [routerLink]="item.route"
                   [queryParams]="item.queryParams || {}"
                   routerLinkActive="active"
                   [routerLinkActiveOptions]="{ exact: item.exact }"
                   [matTooltip]="!item.route ? item.label + ' - Coming soon' : sidebarCollapsed() ? item.label : ''"
                   matTooltipPosition="right"
                   (click)="closeMobileNav()">
                  <mat-icon>{{ item.icon }}</mat-icon>
                  <span class="nav-label">{{ item.label }}</span>
                </a>
              </li>
            }
          }
        </ul>

        @if (secondaryNav().length) {
          <div class="nav-section-title">More</div>
          <ul class="nav-list">
            @for (item of secondaryNav(); track item.label) {
              <li>
                <a class="nav-link"
                   [routerLink]="item.route"
                   [queryParams]="item.queryParams || {}"
                   routerLinkActive="active"
                   [routerLinkActiveOptions]="{ exact: item.exact }"
                   [matTooltip]="sidebarCollapsed() ? item.label : ''"
                   matTooltipPosition="right"
                   (click)="closeMobileNav()">
                  <mat-icon>{{ item.icon }}</mat-icon>
                  <span class="nav-label">{{ item.label }}</span>
                </a>
              </li>
            }
          </ul>
        }

        @if (quickCreate().length) {
          <div class="nav-section-title">Quick Create</div>
          <ul class="nav-list quick-create">
            @for (item of quickCreate(); track item.label) {
              <li>
                <a class="nav-link quick-link"
                   [routerLink]="item.route"
                   [matTooltip]="sidebarCollapsed() ? item.label : ''"
                   matTooltipPosition="right"
                   (click)="closeMobileNav()">
                  <mat-icon>{{ item.icon }}</mat-icon>
                  <span class="nav-label">{{ item.label }}</span>
                </a>
              </li>
            }
          </ul>
        }
      </nav>

      <div class="sidebar-footer">
        <button class="collapse-btn" (click)="toggleSidebar()"
                [matTooltip]="sidebarCollapsed() ? 'Expand' : 'Collapse'" matTooltipPosition="right"
                [attr.aria-label]="sidebarCollapsed() ? 'Expand sidebar' : 'Collapse sidebar'">
          <mat-icon>{{ sidebarCollapsed() ? 'chevron_right' : 'chevron_left' }}</mat-icon>
          <span class="nav-label">Collapse</span>
        </button>
        <span class="sidebar-version">QMS {{ appVersion() || '…' }}</span>
      </div>
    </aside>

    @if (mobileNavOpen()) {
      <div class="nav-overlay" (click)="closeMobileNav()"></div>
    }

    <!-- Main content -->
    <main class="shell-content">
      <div class="content-wrapper">
        <ng-content />
      </div>

      <footer class="footer">
        <div class="footer-content">
          <span>&copy; {{ currentYear }} Chevron Corporation</span>
          <span class="separator">|</span>
          <span>Company Confidential</span>
          <span class="separator">|</span>
          <span>OpCo: Technical Center</span>
          <span class="separator">|</span>
          <span>Technical contact: <a href="javascript:void(0)">Support Request</a></span>
          <span class="separator">|</span>
          <span>Content contact: Surface-FE-QMS</span>
          <span class="separator">|</span>
          <span>Version: {{ appVersion() || 'Loading...' }}</span>
        </div>
      </footer>
    </main>
  </div>
</div>
